import Vue from 'vue'
import Router, { Route, RawLocation, RouteConfig } from 'vue-router'
import Home from './views/Home.vue'
import store from './store'
import { withLangPrefix } from './i18n-route-tree'
import {
  isValidLanguage,
  langOfPath,
  prefixPath,
  stripLang,
  withLang,
} from './i18n-route'
import {
  registerRouteLangGetter,
  getLanguage,
  setStoredLanguage,
} from './utils'
import { applyLocale } from './locales'

Vue.use(Router)

// Route table in absolute-path form. The locale prefix is NOT written here -
// it is derived by withLangPrefix() below, so adding a route needs no
// knowledge of i18n routing.
const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'home',
    component: Home,
  },
  {
    path: '/login',
    name: 'login',
    meta: { public: true },
    component: () =>
      import(/* webpackChunkName: "login" */ './views/Login.vue'),
  },
  {
    path: '/customer/create',
    name: 'customer-create',
    component: () =>
      import(/* webpackChunkName: "customer" */ './views/Customer/create.vue'),
  },
  {
    path: '/customer',
    name: 'customer-list',
    component: () =>
      import(/* webpackChunkName: "customer" */ './views/Customer/index.vue'),
    children: [
      {
        path: '/customer/:id/edit',
        name: 'customer-edit',
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "customer" */ './views/Customer/edit.vue'
          ),
      },
      {
        path: '/customer/:id',
        name: 'customer-detail',
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "customer" */ './views/Customer/detail.vue'
          ),
      },
    ],
  },
  {
    path: '/user',
    name: 'user-list',
    component: () =>
      import(/* webpackChunkName: "user" */ './views/User/index.vue'),
    children: [
      {
        path: '/user/:id',
        name: 'user-detail',
        props: true,
        component: () =>
          import(/* webpackChunkName: "user" */ './views/User/detail.vue'),
      },
    ],
  },
  {
    path: '/subscription',
    name: 'subscription-list',
    component: () =>
      import(
        /* webpackChunkName: "subscription" */ './views/Subscription/index.vue'
      ),
  },
  {
    path: '/reservation',
    name: 'reservation-list',
    component: () =>
      import(
        /* webpackChunkName: "reservation" */ './views/Reservation/index.vue'
      ),
  },
  {
    path: '/reservation_today',
    name: 'reservation-today',
    component: () =>
      import(
        /* webpackChunkName: "reservation" */ './views/Reservation/selectShop.vue'
      ),
  },
  {
    path: '/reservation_today/:shopId',
    name: 'reservation-today-shop',
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "reservation" */ './views/Reservation/today.vue'
      ),
  },
  {
    path: '/timeline',
    name: 'timeline-shop-selection',
    component: () =>
      import(
        /* webpackChunkName: "timeline" */ './views/Timeline/ShopSelection.vue'
      ),
  },
  {
    path: '/timeline/:shopId',
    name: 'timeline-chart',
    props: true,
    component: () =>
      import(/* webpackChunkName: "timeline" */ './views/Timeline/Chart.vue'),
  },
  {
    path: '/kpi2',

    component: () =>
      import(/* webpackChunkName: "kpi" */ './views/KPI2/daily.vue'),
  },
  {
    path: '/kpi',
    component: () =>
      import(/* webpackChunkName: "kpi" */ './views/KPI/index.vue'),
    children: [
      {
        path: '',
        component: () =>
          import(/* webpackChunkName: "kpi" */ './views/KPI/daily.vue'),
      },
      {
        path: 'monthly',
        component: () =>
          import(/* webpackChunkName: "kpi" */ './views/KPI/monthly.vue'),
      },
    ],
  },
  {
    path: '/logs',
    props: true,
    component: () =>
      import(/* webpackChunkName: "logs" */ './views/ReservationLog.vue'),
  },
  {
    path: '/card',
    component: () =>
      import(/* webpackChunkName: "timeline" */ './views/Dev.vue'),
  },
  {
    path: '/holiday',
    component: () =>
      import(/* webpackChunkName: "business_hours" */ './views/Holiday.vue'),
  },
  {
    path: '/openday',
    component: () =>
      import(/* webpackChunkName: "business_hours" */ './views/OpenDay.vue'),
  },
  {
    path: '/send_waku',
    component: () =>
      import(/* webpackChunkName: "business_hours" */ './views/Hotpepper.vue'),
  },
  {
    path: '/goods/purchase',
    component: () =>
      import(/* webpackChunkName: "goods" */ './views/Goods/Purchase.vue'),
  },
  {
    path: '/timecard',
    component: () =>
      import(/* webpackChunkName: "timecard" */ './views/TimeCard.vue'),
  },
  {
    path: '/overflow',
    component: () =>
      import(/* webpackChunkName: "overflow" */ './views/OverflowKPI.vue'),
  },
  {
    path: '/shift',
    component: () =>
      import(/* webpackChunkName: "shift" */ './views/Shift.vue'),
  },
  {
    path: '/previous_color',
    component: () =>
      import(/* webpackChunkName: "prev" */ './views/PreviousColor/index.vue'),
  },
  {
    path: '/previous_color/:shopId',
    props: true,
    component: () =>
      import(/* webpackChunkName: "prev" */ './views/PreviousColor/detail.vue'),
  },

  {
    path: '/ec_user',
    component: () => import(/* webpackChunkName: "ec" */ './views/ECUser.vue'),
  },
  {
    path: '/additional_waku',
    component: () =>
      import(/* webpackChunkName: "ec" */ './views/ForceReservable.vue'),
  },
  {
    path: '/merge_customer',
    component: () =>
      import(/* webpackChunkName: "customer" */ './views/CustomerMerge.vue'),
  },
  {
    path: '/used_ticket',
    component: () =>
      import(/* webpackChunkName: "ticket" */ './views/UsedTicket.vue'),
  },
  {
    path: '/purchased_ticket',
    component: () =>
      import(/* webpackChunkName: "ticket" */ './views/PurchasedTicket.vue'),
  },
  {
    path: '/firebase_user',
    component: () =>
      import(/* webpackChunkName: "user" */ './views/FirebaseUser/index.vue'),
  },
  {
    path: '/firebase_user/:uid',
    component: () =>
      import(/* webpackChunkName: "user" */ './views/FirebaseUser/detail.vue'),
  },
  {
    path: '/auth',
    name: 'auth',
    meta: { public: true },
    async beforeEnter(to, from, next) {
      const api = Vue.prototype.$api
      const token = to.query.token
      const path = (to.query.next || '/') as string
      // Same fix as the auth guard in main.ts: to.params.lang is the locale
      // for THIS navigation (the lang guard runs before beforeEnter), and
      // must be carried forward explicitly or the redirect below re-derives
      // the locale from the last confirmed route.
      const lang = (to.params.lang as string) || getLanguage()
      if (!token) {
        next(
          withLang({ name: 'login', query: { next: stripLang(path) } }, lang)
        )
      } else {
        try {
          let data = await api.auth.refreshToken({ token })
          store.commit('LOGIN', data)
          // path (to.query.next) is deliberately locale-free; withLang is
          // idempotent, so this is a no-op if it somehow already has one.
          next(withLang(path, lang))
        } catch (err) {
          next(
            withLang({ name: 'login', query: { next: stripLang(path) } }, lang)
          )
        }
      }
    },
  },
]

const router = new Router({
  routes: withLangPrefix(routes),
})

// The lang guard's side effects (persisting the locale, applying it to the
// non-reactive consumers - moment, <html lang>, Vuetify) need a DOM and
// localStorage. Defaulted to the real implementations for production, and
// left injectable so a test can swap in no-op/in-memory stubs and install
// the REAL guard (installLangGuard below, unmodified) onto a throwaway
// abstract-mode router without a DOM. isValidLanguage and prefixPath are
// left as direct imports rather than deps - they are already pure functions
// from i18n-route.ts, so there is nothing in them that needs stubbing.
export interface LangGuardDeps {
  getLanguage: () => string
  setStoredLanguage: (lang: string) => void
  applyLocale: (lang: string) => void
  // The hash the address bar shows RIGHT NOW, without its leading '#'. Needed
  // to tell apart the two ways this guard is reached; see the replace/push
  // decision below. A dep rather than a direct window read so the guard still
  // runs on a DOM-less abstract-mode router.
  currentHash: () => string
}

const defaultLangGuardDeps: LangGuardDeps = {
  getLanguage,
  setStoredLanguage,
  applyLocale,
  currentHash: () =>
    typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, ''),
}

// Installs the guard that keeps the URL's locale segment authoritative:
// valid lang -> apply it and continue; unmatched (404-shaped) URL -> leave
// it alone; anything else (a real route reached without a locale) -> rewrite
// the path to carry one.
//
// Exported (rather than kept as an inline router.beforeEach) so a test can
// install this exact guard onto its own abstract-mode router and assert on
// its behaviour instead of copying the logic into the test - a copied guard
// would test the copy, not the guard.
export function installLangGuard(
  router: Router,
  deps: LangGuardDeps = defaultLangGuardDeps
): void {
  router.beforeEach((to, from, next) => {
    const lang = to.params.lang as string | undefined
    if (isValidLanguage(lang) && lang) {
      deps.setStoredLanguage(lang)
      deps.applyLocale(lang)
      next()
      return
    }

    // Nothing matched, so this is a 404-shaped URL (the locale regex rejects
    // anything outside ja|en|th, and there is no catch-all route). Prefixing
    // it would still not match, so the path is left exactly as typed.
    //
    // The locale still has to be decided, and params.lang is empty here, so
    // it is read off the raw path: a URL that spells out its locale must win
    // over the stored one even when the rest of it is a typo. getLanguage()
    // cannot do this - it resolves URL -> stored -> browser, and its URL step
    // reads router.currentRoute, which during beforeEach is still the route
    // being left. So #/ja/customerr used to render in whatever locale was
    // already on screen while the URL claimed ja, which also left the
    // language menu ticking the wrong entry.
    if (to.matched.length === 0) {
      const fromPath = langOfPath(to.path)
      if (fromPath) deps.setStoredLanguage(fromPath)
      deps.applyLocale(fromPath || deps.getLanguage())
      next()
      return
    }

    // A real route reached without a locale. Build the path explicitly:
    // vue-router only honours `params` when the location also carries a
    // `name`, and ~21 of the 37 routes have no name.
    //
    // Whether to replace or push turns on ONE question: does a history entry
    // for this navigation already exist? Two situations reach this line and
    // they need opposite answers, so neither a bare true nor a bare false is
    // correct - MEASURED, both ways round, before this was written.
    //
    //  - The address bar ALREADY shows this URL: the user edited the hash, or
    //    followed a link the router did not intercept. The browser created the
    //    entry before any guard ran. Pushing would add a SECOND entry for the
    //    same page, so Back lands on the locale-less URL and gets rewritten
    //    forward again - the Back button looks dead until pressed twice.
    //  - A guard called next(location) without a locale. vue-router aborts
    //    that transition before it touches the address bar (HashHistory.push
    //    calls pushHash only inside transitionTo's success callback), so no
    //    entry exists. Replacing would overwrite the PREVIOUS page's entry:
    //    measured on a redirect to /kpi, history.length stayed at 4 and Back
    //    skipped straight past the page the user came from.
    //
    // from.matched.length === 0 is vue-router's START_LOCATION sentinel - the
    // very first transition, where the entry likewise already exists. It is a
    // separate clause because a first load of '/' has an empty location.hash,
    // which the comparison below cannot recognise.
    //
    // Every call site that redirects without a locale today goes through
    // withLang() (grep `next(`), so the second situation should not arise -
    // but that is an observation about today's code, not an invariant anything
    // enforces, and it is exactly the assumption that made a bare `true` look
    // safe.
    const addressBarAlreadyShowsTarget = (): boolean => {
      const shown = deps.currentHash()
      try {
        return decodeURI(shown) === decodeURI(to.fullPath)
      } catch {
        return shown === to.fullPath
      }
    }

    next({
      path: prefixPath(to.path, deps.getLanguage()),
      query: to.query,
      hash: to.hash,
      replace: from.matched.length === 0 || addressBarAlreadyShowsTarget(),
    })
  })
}

// Wraps router.push/replace/resolve so the ~34 existing call sites keep
// working unchanged - including router-link, which calls router.push to
// navigate and router.resolve to build the href it renders.
//
// IMPORTANT: this wrapper does not cover every navigation. When a guard
// (beforeEach, beforeEnter, beforeRouteEnter) calls next(location), vue-
// router invokes History.prototype.push/replace directly - not
// router.push/replace - so guard-issued redirects bypass this wrapper
// entirely and must carry the locale themselves (see the /auth beforeEnter
// above, and the beforeRouteEnter guards in ShopSelection.vue, selectShop.vue
// and PreviousColor/index.vue).
//
// Exported for the same reason as installLangGuard: so the test can install
// the real wrapper onto its own router instead of reimplementing it.
export function wrapNavigation(
  router: Router,
  getLanguageFn: () => string = getLanguage
): void {
  const originalPush = router.push.bind(router)
  const originalReplace = router.replace.bind(router)
  const originalResolve = router.resolve.bind(router)

  const activeLang = (): string =>
    (router.currentRoute.params.lang as string) || getLanguageFn()

  // Mirror vue-router's own push/replace overloads exactly (rather than
  // casting the whole function through `never`) so the wrapper still checks
  // arity/argument types at call sites.
  function wrappedPush(to: RawLocation): Promise<Route>
  function wrappedPush(
    to: RawLocation,
    onComplete?: (route: Route) => void,
    onAbort?: (err: Error) => void
  ): void
  function wrappedPush(
    to: RawLocation,
    onComplete?: (route: Route) => void,
    onAbort?: (err: Error) => void
  ): Promise<Route> | void {
    return originalPush(withLang(to, activeLang()), onComplete, onAbort)
  }

  function wrappedReplace(to: RawLocation): Promise<Route>
  function wrappedReplace(
    to: RawLocation,
    onComplete?: (route: Route) => void,
    onAbort?: (err: Error) => void
  ): void
  function wrappedReplace(
    to: RawLocation,
    onComplete?: (route: Route) => void,
    onAbort?: (err: Error) => void
  ): Promise<Route> | void {
    return originalReplace(withLang(to, activeLang()), onComplete, onAbort)
  }

  // router-link does NOT build its href from push - it calls router.resolve
  // and renders the returned href on the <a>. resolve() therefore has to be
  // wrapped too, or every <router-link> in the app renders a locale-less
  // href even though clicking it navigates correctly (push adds the locale).
  // That gap is only visible when the browser follows the href itself
  // instead of going through push: ctrl/middle-click, "open link in new
  // tab", "copy link address", and the URL shown on hover. Those would
  // resolve their locale from localStorage - shared across tabs - rather
  // than from the locale this tab is showing.
  //
  // Wrapping resolve also makes router-link-active match on the prefixed
  // path, which is what the links actually navigate to.
  function wrappedResolve(
    to: RawLocation,
    current?: Route,
    append?: boolean
  ): ReturnType<Router['resolve']> {
    return originalResolve(withLang(to, activeLang()), current, append)
  }

  router.push = wrappedPush
  router.replace = wrappedReplace
  router.resolve = wrappedResolve
}

// The one implementation behind every "switch language" control - the third
// piece of the same invariant as installLangGuard() and wrapNavigation()
// above, which is why it lives here rather than in a module of its own.
// App.vue's app-bar menu and Login.vue's language row held a byte-identical
// copy of it each; the URL-repair rules below are subtle enough that two
// copies were guaranteed to drift.
export function switchLocale(vm: Vue, language: string): void {
  if (language === vm.$i18n.locale) return

  // A 404-shaped URL has no matched record and no route name, so vue-router 3
  // cannot resolve the relative-params navigation below: it strands the user
  // on a blank "/" and then rejects every later switch as redundant, leaving
  // the language menu permanently dead. Apply the locale directly instead,
  // then repair the URL by path.
  if (vm.$route.matched.length === 0) {
    setStoredLanguage(language)
    applyLocale(language)
    // The URL is dead either way, but if it still carries a locale segment
    // (#/ja/customerr) that segment has to follow the language on screen -
    // otherwise the URL claims ja while the UI reads Thai, and sharing it
    // hands the reader the wrong language. Navigating by path (not by
    // relative params) is what makes this work here: the path matcher runs,
    // so vue-router keeps the path instead of collapsing it to "/". A URL
    // with no locale segment (#/customerr) or a junk first segment
    // (#/zz/...) has nothing to swap and is left exactly as typed.
    const { fullPath } = vm.$route
    const withoutLang = stripLang(fullPath)
    if (withoutLang !== fullPath)
      vm.$router
        .replace(prefixPath(withoutLang, language))
        // The target is a dead URL either way, so a rejection here can only
        // be an aborted navigation - nothing to recover from.
        .catch(() => undefined)
    return
  }

  // The lang guard applies the locale; replace() keeps Back from bouncing
  // between two languages of the same page.
  vm.$router
    .replace({
      params: { ...vm.$route.params, lang: language },
      // query/hash MUST be explicit: vue-router 3's "relative params" fast
      // path in normalizeLocation copies current.params but not
      // current.query, so omitting them drops every filter and page number
      // on a language switch.
      query: vm.$route.query,
      hash: vm.$route.hash,
    })
    // Defensive only, and deliberately kept asymmetric-looking no longer:
    // the 404 branch above needs its catch because it navigates to a dead
    // URL. This one has no KNOWN way to reject. MEASURED (2026-09-07): two
    // switchLocale() calls in the same tick do not race - a params-only
    // change on the already-loaded current route runs every guard
    // synchronously, so the first transition has fully confirmed
    // (currentRoute and $i18n.locale already updated) before the second is
    // invoked, and both promises resolve. So this catch is insurance against
    // a future async guard or lazily-loaded route making the transition
    // await, not a fix for an observed unhandled rejection - do not cite it
    // as one.
    .catch(() => undefined)
}

wrapNavigation(router)

registerRouteLangGetter(
  () => (router.currentRoute.params.lang as string) || null
)

// Registered here rather than in main.ts so it runs BEFORE the auth guard:
// this module is evaluated when main.ts imports it.
installLangGuard(router)

export default router
